import React, { useState } from "react";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import { Content, HtmlContent } from "./Content";
import ArrowDownIcon from "./Icons/ArrowDown";
import { Helmet } from "react-helmet";
import { ContentAndVideo } from "./ContentAndVideo";

const FaqsStyles = styled.ol`
  ${({ ...props }) => css`
    li {
      border-top: solid 1px ${props.theme.colors.lightGrey};

      &:last-child {
        border-bottom: solid 1px ${props.theme.colors.lightGrey};
      }
      .section--grey & {
        border-color: ${props.theme.colors.darkPrimary}
      }
    }
  `}
`;

const ButtonStyles = styled.button`
  ${({ ...props }) => css`
    background: none;
    color: ${props.theme.colors.primary};
    text-transform: unset;
    font-size: inherit;
    letter-spacing: inherit;
    padding: 8px 16px;
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: inherit;
    transition: all ease-in-out 0.3s;

    &:hover {
      background: ${props.theme.colors.lightGrey};
      color: ${props.theme.colors.darkPrimary};
    }

    &:focus {
      outline: none;
    }

    svg {
      flex-shrink: 0;
      flex-grow: 0;
      margin-left: 24px;
      transition: all ease 0.3s;
      transform: rotate(${(props.$opened ? "180deg" : "0deg")});
    }

    border-radius: 0px;

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding: 16px;
    }
  `}
`;

const ContentStyles = styled.div`
  ${({ ...props }) => css`
    display: ${(props.$opened ? "block" : "none")};
    padding: 24px 16px;
  `}
`;

const AccordionItem = ({ index, title, content, video }) => {
  const [isOpen, setOpen] = useState(false);

  const buttonID = `drawer-button-${index}`;
  const panelID = `drawer-panel-${index}`;

  return (
    <li>
      <h3>
        <ButtonStyles
          type="button"
          id={buttonID}
          aria-controls={panelID}
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
          $opened={isOpen}
        >
          <span><HtmlContent htmlString={title} /></span>
          <ArrowDownIcon />
        </ButtonStyles>
      </h3>
      <ContentStyles
        id={panelID}
        tabIndex={!isOpen ? -1 : 'auto'}
        aria-labelledby={buttonID}
        aria-hidden={!isOpen}
        $opened={isOpen}
      >
        {video ? (
          <ContentAndVideo content={content} video={video} />
        ) : (
          <Content content={content} />
        )}
      </ContentStyles>
    </li>
  );
};

export const Faqs = ({ items, fullWidth = false, background = 'white' }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <></>;
  }

  return (
    <Section
      heading="Frequently asked questions"
      grey={background === 'grey'}
    >
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                ${items.map(
                  (item) =>
                    `{
                    "@type": "Question",
                    "name": "${item.faqQuestion}",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${item.answer.faqAnswer?.replace(
                        /(<([^>]+)>)/gi,
                        ""
                      )}"
                    }
                  }`
                )}
                
              ]
            }
          `}
        </script>
      </Helmet>
      <Container
        slim={!fullWidth}
      >
        <FaqsStyles>
          {items.map((item, index) => (
            <AccordionItem
              key={item.faqQuestion}
              index={index}
              title={item.faqQuestion}
              content={item.answer.faqAnswer}
              video={item.answer.faqVideo}
            />
          ))}
        </FaqsStyles>
      </Container>
    </Section>
  );
};
